import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, type SyntheticEvent } from 'react';
import { tenantListQuery } from '../api/platform';
import { hasCapability, sessionQuery } from '../api/session';
import { TenantStatusBadge } from '../components/status';
import { he } from '../i18n/he';
import { errorMessage } from '../lib/format';
import {
  Alert,
  Button,
  Card,
  Cell,
  formatDate,
  Loading,
  PageHeader,
  Table,
  TextField,
} from '@avrahami/ui';

export function TenantsPage() {
  const session = useQuery(sessionQuery);
  const [draft, setDraft] = useState('');
  const [slug, setSlug] = useState('');
  const tenants = useInfiniteQuery(tenantListQuery(slug));
  const canCreate =
    session.data !== undefined && hasCapability(session.data, 'platform.tenants.create');

  const onSearch = (event: SyntheticEvent) => {
    event.preventDefault();
    setSlug(draft.trim().toLowerCase());
  };
  const items = tenants.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <PageHeader
        title={he.tenants.title}
        actions={
          canCreate ? (
            <Link
              to="/tenants/new"
              className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              {he.tenants.create}
            </Link>
          ) : null
        }
      />
      <Card>
        <form onSubmit={onSearch} className="mb-4 flex flex-wrap items-end gap-2">
          <TextField
            label={he.tenants.search}
            name="slug"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            className="ltr w-64"
          />
          <Button type="submit" tone="secondary">
            {he.tenants.searchSubmit}
          </Button>
          {slug === '' ? null : (
            <Button
              tone="secondary"
              onClick={() => {
                setDraft('');
                setSlug('');
              }}
            >
              {he.tenants.clear}
            </Button>
          )}
        </form>
        {tenants.isPending ? <Loading /> : null}
        {tenants.isError ? <Alert>{errorMessage(tenants.error)}</Alert> : null}
        {tenants.isSuccess && items.length === 0 ? (
          <p className="text-sm text-slate-500">{he.tenants.empty}</p>
        ) : null}
        {items.length > 0 ? (
          <Table
            head={[
              he.tenants.name,
              he.tenants.slug,
              he.tenants.kind,
              he.tenants.status,
              he.tenants.created,
            ]}
          >
            {items.map((tenant) => (
              <tr key={tenant.id} data-testid="tenant-row" data-slug={tenant.slug}>
                <Cell>
                  <Link
                    to="/tenants/$tenantId"
                    params={{ tenantId: tenant.id }}
                    className="font-medium text-brand-700 hover:underline"
                  >
                    {tenant.displayName}
                  </Link>
                </Cell>
                <Cell className="ltr text-slate-600">{tenant.slug}</Cell>
                <Cell>{he.tenantKind[tenant.kind]}</Cell>
                <Cell>
                  <TenantStatusBadge status={tenant.status} />
                </Cell>
                <Cell>{formatDate(tenant.createdAt)}</Cell>
              </tr>
            ))}
          </Table>
        ) : null}
        {tenants.hasNextPage ? (
          <div className="mt-4">
            <Button
              tone="secondary"
              busy={tenants.isFetchingNextPage}
              onClick={() => {
                void tenants.fetchNextPage();
              }}
            >
              {he.common.loadMore}
            </Button>
          </div>
        ) : null}
      </Card>
    </>
  );
}
